'use client';

import { RefObject } from 'react';
import { useId } from 'react';
import { Link } from '@/ui-components/atoms/Link/Link';
import { Text } from '@/ui-components/atoms/Text/Text';
import {
    SnapshotAlertBanner,
    type SnapshotAlertBannerProps,
    type SnapshotAlertBannerTone,
} from './SnapshotAlertBanner';
import { SnapshotAlertList } from './SnapshotAlertList';
import { SnapshotPopoverShell } from './SnapshotPopoverShell';
import { buildCareRouteUrl } from './careAccountUrls';
import styles from './snapshotpopover.module.scss';

const SIGN_IN_URL = buildCareRouteUrl('accountCenterLogin');

export interface SnapshotAlertItem {
    ctaHref?: string;
    ctaLabel?: string;
    id: string;
    message: string;
    tone?: SnapshotAlertBannerTone;
}

interface SnapshotAlertPopoverProps {
    alerts?: SnapshotAlertItem[];
    buttonRef: RefObject<HTMLButtonElement | null>;
    emptyState?: React.ReactNode;
    featuredAlert?: SnapshotAlertBannerProps;
    heading?: string;
    id?: string;
    open: boolean;
    onClose: () => void;
}

export function SnapshotAlertPopover({
    alerts,
    buttonRef,
    emptyState,
    featuredAlert,
    heading = 'Your alerts',
    id,
    open,
    onClose,
}: SnapshotAlertPopoverProps): React.JSX.Element | null {
    const headingId = useId();
    const hasAlerts = !!alerts?.length;
    const hasFeaturedAlert = !!featuredAlert;
    let bodyContent: React.JSX.Element | null = null;

    if (hasFeaturedAlert) {
        bodyContent = (
            <>
                <div className={styles.snapshotDivider}>
                    <div className={styles.snapshotDividerLine} />
                </div>
                <SnapshotAlertBanner {...featuredAlert} />
                {hasAlerts && <SnapshotAlertList alerts={alerts} />}
            </>
        );
    } else if (hasAlerts) {
        bodyContent = <SnapshotAlertList alerts={alerts} />;
    } else if (emptyState) {
        bodyContent = (
            <>
                <div className={styles.snapshotDivider}>
                    <div className={styles.snapshotDividerLine} />
                </div>
                <div className={styles.snapshotCenteredBody}>{emptyState}</div>
            </>
        );
    } else {
        bodyContent = (
            <>
                <div className={styles.snapshotDivider}>
                    <div className={styles.snapshotDividerLine} />
                </div>
                <div className={styles.snapshotCenteredBody}>
                    <Text tag="p" className={styles.snapshotCenteredHeading}>
                        Stay in the know
                    </Text>
                    <Text tag="p" className={styles.snapshotCenteredCopy}>
                        <Link className={styles.snapshotAlertInlineLink} href={SIGN_IN_URL}>
                            Sign in
                        </Link>{' '}
                        for personalized reminders
                    </Text>
                </div>
            </>
        );
    }

    return (
        <SnapshotPopoverShell
            ariaLabel={heading}
            ariaLabelledBy={headingId}
            buttonRef={buttonRef}
            id={id}
            onClose={onClose}
            open={open}
        >
            <div className={styles.snapshotSectionHeader}>
                <Text tag="h3" className={styles.snapshotTitle} id={headingId}>
                    {heading}
                </Text>
            </div>
            {bodyContent}
        </SnapshotPopoverShell>
    );
}
